/**
 * JSON-LD Structured Data Templates
 * 
 * Provides reusable templates for common Schema.org types
 * https://schema.org/
 */

/**
 * Organization Schema
 * Use on homepage and about page
 */
export function createOrganizationSchema({
  name = 'VERVEAI',
  url = 'https://verveai.co',
  logo = 'https://verveai.co/logo.png',
  description = 'AI-powered tools and resources platform',
  sameAs = []
}) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name,
    url,
    logo: {
      '@type': 'ImageObject',
      url: logo
    },
    description,
    sameAs
  };
}

/**
 * WebSite Schema
 * Use on homepage
 */
export function createWebSiteSchema({
  name = 'VERVEAI',
  url = 'https://verveai.co',
  description = 'Turnkey startup launchpad for newly incorporated companies',
  searchUrl = 'https://verveai.co/blog?search={search_term_string}'
}) {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name,
    url,
    description,
    potentialAction: {
      '@type': 'SearchAction',
      target: searchUrl,
      'query-input': 'required name=search_term_string'
    }
  };
}

/**
 * Article Schema
 * Use for blog posts and news articles
 */
export function createArticleSchema({
  headline,
  description,
  image,
  datePublished,
  dateModified,
  author = 'VerveAI Team',
  url
}) {
  const imageUrl = image
    ? (image.startsWith('http') ? image : `https://verveai.co${image.startsWith('/') ? '' : '/'}${image}`)
    : 'https://verveai.co/logo.png';

  return {
    '@type': 'Article',
    headline,
    description,
    image: imageUrl,
    datePublished,
    dateModified: dateModified || datePublished,
    author: {
      '@type': 'Organization',
      name: author,
      url: 'https://verveai.co'
    },
    publisher: {
      '@type': 'Organization',
      name: 'VerveAI',
      url: 'https://verveai.co',
      logo: {
        '@type': 'ImageObject',
        url: 'https://verveai.co/logo.png'
      }
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': url
    }
  };
}

/**
 * Product/SoftwareApplication Schema
 * Use for individual tool pages
 */
export function createSoftwareApplicationSchema({
  name,
  description,
  url,
  image,
  category,
  operatingSystem = 'Web',
  applicationCategory = 'BusinessApplication',
  offers = null
}) {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name,
    description,
    url,
    image,
    applicationCategory,
    operatingSystem
  };

  if (category) {
    schema.applicationSubCategory = category;
  }

  if (offers) {
    schema.offers = {
      '@type': 'Offer',
      price: offers.price || '0',
      priceCurrency: offers.currency || 'USD',
      availability: 'https://schema.org/InStock'
    };
  }

  return schema;
}

/**
 * BreadcrumbList Schema
 * Use on all pages for navigation breadcrumbs
 */
export function createBreadcrumbSchema(items) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: item.url
    }))
  };
}

/**
 * FAQPage Schema
 * Use for FAQ pages or pages with Q&A content
 */
export function createFAQSchema(faqs) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map(faq => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer
      }
    }))
  };
}

/**
 * CollectionPage Schema
 * Use for tool directory/listing pages
 */
export function createCollectionPageSchema({
  name,
  description,
  url,
  numberOfItems
}) {
  return {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name,
    description,
    url,
    ...(numberOfItems && { numberOfItems })
  };
}

/**
 * ItemList Schema
 * Use for lists of tools or resources
 */
export function createItemListSchema({
  name,
  description,
  items
}) {
  return {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name,
    description,
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      url: item.url,
      name: item.name
    }))
  };
}

/**
 * Review Schema
 * Use for tool reviews
 */
export function createReviewSchema({
  itemName,
  itemUrl,
  rating,
  reviewBody,
  author = 'VERVEAI',
  datePublished
}) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Review',
    itemReviewed: {
      '@type': 'SoftwareApplication',
      name: itemName,
      url: itemUrl
    },
    reviewRating: {
      '@type': 'Rating',
      ratingValue: rating,
      bestRating: '5',
      worstRating: '1'
    },
    reviewBody,
    author: {
      '@type': 'Organization',
      name: author
    },
    datePublished
  };
}

/**
 * HowTo Schema
 * Use for tutorial/guide pages
 */
export function createHowToSchema({
  name,
  description,
  image,
  steps
}) {
  return {
    '@context': 'https://schema.org',
    '@type': 'HowTo',
    name,
    description,
    image,
    step: steps.map((step, index) => ({
      '@type': 'HowToStep',
      position: index + 1,
      name: step.name,
      text: step.text,
      ...(step.image && { image: step.image })
    }))
  };
}

/**
 * Combine multiple schemas
 * Use when you need multiple schema types on one page
 */
export function combineSchemas(...schemas) {
  return {
    '@context': 'https://schema.org',
    '@graph': schemas.flat().filter(Boolean)
  };
}

export default {
  createOrganizationSchema,
  createWebSiteSchema,
  createArticleSchema,
  createSoftwareApplicationSchema,
  createBreadcrumbSchema,
  createFAQSchema,
  createCollectionPageSchema,
  createItemListSchema,
  createReviewSchema,
  createHowToSchema,
  combineSchemas
};
